import { TrackSkeleton } from "../track-skeleton";
import { lineConfig, pointNames } from "@/shared/consts";
import { observer } from "@legendapp/state/react";
import { useDetectionsBuffer } from "@/services/buffer-controller";
import { useTimelinePlayback } from "@/contexts/playback-context";
import { getUniqueColor } from "@/shared";
import { useMemo } from "react";
import { useViewSettings } from "@/view-settings";
import { Track } from "../types";
import {
  UNMATCHED_DETECTION_COLOR,
  UNMATCHED_DETECTION_TRACK_ID,
  getDateTime,
} from "@/services/buffer-controller/utils";
import { TrackID } from "@/shared/types";

export const Detection = observer(
  ({ trackId }: { trackId: TrackID; debug?: boolean }) => {
    const detectionsBuffer = useDetectionsBuffer();
    const timelinePlayback = useTimelinePlayback();
    const currentTime = timelinePlayback.currentTime.get();
    const snapshot = detectionsBuffer.getSnapshotToDraw(trackId, currentTime);
    const color = useMemo(
      () =>
        trackId == UNMATCHED_DETECTION_TRACK_ID
          ? UNMATCHED_DETECTION_COLOR
          : getUniqueColor(trackId).string,
      [trackId]
    );
    const viewSettings = useViewSettings();
    const confidence = viewSettings.detectionConfidence.get();
    const showUnmatchedDetections = viewSettings.showUnmatchedDetections.get();

    if (!showUnmatchedDetections && trackId === UNMATCHED_DETECTION_TRACK_ID)
      return null;

    if (!snapshot) return null;

    const track: Track = {
      pose_x: snapshot.object.joints.map((pose) => (16 * pose.x) / 1920 - 8),
      pose_y: snapshot.object.joints.map((pose) => (9 * pose.y) / 1080 - 4.5),
      pose_z: snapshot.object.joints.map(() => 0),
      pose_conf: snapshot.object.joints.map((pose) => pose.conf),
    };

    const shouldSkipPoint = (index: number) => {
      const { x, y, conf } = snapshot.object.joints[index];
      return x === 0 && y === 0 && conf === 0;
    };

    const pointPopupContent = (selectedIndex: number) => (
      <ul>
        <li className="whitespace-nowrap">Track: {trackId}</li>
        <li className="whitespace-nowrap">Name: {pointNames[selectedIndex]}</li>
        <li className="whitespace-nowrap">
          X: {snapshot.object.joints[selectedIndex].x}
        </li>
        <li className="whitespace-nowrap">
          Y: {snapshot.object.joints[selectedIndex].y}
        </li>
        <li className="whitespace-nowrap">
          Conf: {snapshot.object.joints[selectedIndex].conf}
        </li>
        <li className="whitespace-nowrap">Message ID: {snapshot.message_id}</li>
        <li className="whitespace-nowrap">
          Detection ID: {snapshot.object.id}
        </li>
        <li className="whitespace-nowrap">
          Detection time: {getDateTime(snapshot)}
        </li>
      </ul>
    );

    return (
      <TrackSkeleton
        lines={lineConfig}
        lineWidth={0.05}
        pointSize={0.2}
        confidence={confidence}
        color={color}
        distanceFactor={10}
        positiveCoords
        track={track}
        lineType="lines"
        shouldSkipPoint={shouldSkipPoint}
        pointPopupContent={pointPopupContent}
      />
    );
  }
);
